import { useState } from 'react';
import { Button } from '@uconnect/assets/components/ui/button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@uconnect/assets/components/ui/dialog';
import Icon from '@uconnect/assets/components/ui/icon';
import { cn } from '@uconnect/assets/lib/utils';

interface ClearHistoryButtonProps {
	isExpanded?: boolean;
	isVisible: boolean;
	isLoading?: boolean;
	isDisabled?: boolean;
	onClick: () => void;
	containerMaxWidthClasses?: string;
	variant?: 'search' | 'admin';
}

export default function ClearHistoryButton( {
	isExpanded = false,
	isVisible,
	isLoading = false,
	isDisabled = false,
	onClick,
	containerMaxWidthClasses = null,
	variant = 'search',
}: ClearHistoryButtonProps ) {
	const [ open, setOpen ] = useState( false );
	const isAdmin = variant === 'admin';
	const labels = {
		trigger: isLoading ? 'Clearing…' : 'Clear History',
		title: isAdmin ? 'Clear admin conversation history?' : 'Clear search history?',
		description: isAdmin
			? 'This removes all saved admin conversations for this site and cannot be undone.'
			: 'This removes all saved conversations and memories, and cannot be undone.',
		confirm: isLoading ? 'Clearing…' : 'Yes, clear history',
	};

	const handleConfirmClear = () => {
		Promise.resolve( onClick() )
			.then( () => {
				setOpen( false );
			} )
			.catch( () => {
				setOpen( false );
			} );
	};

	if ( ! isVisible ) {
		return null;
	}

	return (
		<Dialog open={ open } onOpenChange={ setOpen }>
			<div className={ cn( 'px-3 pt-1 pb-2 -mt-2', isExpanded && 'flex justify-center' ) }>
				<div className={ cn( 'flex justify-end pr-1', isExpanded && containerMaxWidthClasses ) }>
					<DialogTrigger asChild>
						<Button
							variant="link"
							size="sm"
							className="group p-0! flex!"
							disabled={ isLoading || isDisabled }
						>
							<Icon name="trash" className="size-4.5 shrink-0" />
							<span className="group-hover:underline">
								{ labels.trigger }
							</span>
						</Button>
					</DialogTrigger>
				</div>
			</div>
			<DialogContent
				className="sm:max-w-md !p-0 overflow-hidden"
				showCloseButton={ false }
			>
				<div className="relative !p-6">
					<DialogClose asChild>
						<Button
							variant="mutedInterface"
							size="icon"
							className="!absolute top-4 right-4"
							aria-label="Close clear history dialog"
						>
							<Icon name="xmark" className="!size-5" />
						</Button>
					</DialogClose>

					<DialogHeader className="pr-16 !text-left">
						<DialogTitle>{ labels.title }</DialogTitle>
						<DialogDescription className="!my-4">
							{ labels.description }
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="mt-6">
						<DialogClose asChild>
							<Button
								variant="ghost"
								className="px-4! py-2! leading-none!"
							>
								Cancel
							</Button>
						</DialogClose>
						<Button
							variant="destructive"
							onClick={ handleConfirmClear }
							disabled={ isLoading || isDisabled }
							className="min-w-40 px-4! py-2! leading-none!"
						>
							{ labels.confirm }
						</Button>
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	);
}
